import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { DEFAULT_PAGINATION_CONFIG } from '~/lib/constants';
import { useAppDispatch, useAppSelector } from '~/store/hooks';
import api from '~/store/services/api';
import { CacheTag } from '~/store/services/CacheTag';

interface InquiryListSliceState {
  isConflictErrorModalOpen?: boolean;
  isLoading?: boolean;
  numberOfInquiries?: number;
  pageNumber: number;
  pageSize: number;
}

const initialState: InquiryListSliceState = {
  isConflictErrorModalOpen: false,
  isLoading: false,
  numberOfInquiries: undefined,
  pageNumber: 1,
  pageSize: DEFAULT_PAGINATION_CONFIG.pageSize,
};

export const inquiryListSlice = createSlice({
  initialState,
  name: 'inquiryList',
  reducers: {
    resetPagination: (state) => {
      state.pageNumber = initialState.pageNumber;
    },
    setIsConflictErrorModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isConflictErrorModalOpen = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setNumberOfInquiries: (state, action: PayloadAction<number>) => {
      state.numberOfInquiries = action.payload;
    },
    setPageNumber: (state, action: PayloadAction<number>) => {
      state.pageNumber = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
  },
});

export const {
  resetPagination: resetPaginationAction,
  setIsConflictErrorModalOpen: setIsConflictErrorModalOpenAction,
  setIsLoading: setIsLoadingAction,
  setNumberOfInquiries: setNumberOfInquiriesAction,
  setPageNumber: setPageNumberAction,
  setPageSize: setPageSizeAction,
} = inquiryListSlice.actions;

export default inquiryListSlice.reducer;

export const useInquiryListDispatch = () => {
  const dispatch = useAppDispatch();

  const setPageNumber = (currentPage: number) => {
    dispatch(setPageNumberAction(currentPage));
  };

  const setPageSize = (pageSize: number) => {
    dispatch(setPageSizeAction(pageSize));
  };

  const resetPagination = () => {
    dispatch(resetPaginationAction());
  };

  const setIsInquiryListLoading = (isLoading: boolean) => {
    dispatch(setIsLoadingAction(isLoading));
  };

  const setIsConflictErrorModalOpen = (isOpen: boolean) => {
    dispatch(setIsConflictErrorModalOpenAction(isOpen));
  };

  const reloadInquiryList = () => {
    /* NOTE: 
      Exposing this action to allow more control on when to invalidate the cache
      If there's no need for more granular control, use 'invalidateTags' from the api util instead.
    */
    dispatch(api.util.invalidateTags([CacheTag.Inquiries]));
  };

  return {
    reloadInquiryList,
    resetPagination,
    setIsConflictErrorModalOpen,
    setIsInquiryListLoading,
    setPageNumber,
    setPageSize,
  };
};

export const useInquiryListPaginationSelector = () => {
  const pageNumber = useAppSelector((state) => state.inquiryList.pageNumber);
  const pageSize = useAppSelector((state) => state.inquiryList.pageSize);

  return {
    pageNumber,
    pageSize,
  };
};

export const useIsInquiryListLoadingSelector = () => {
  return useAppSelector((state) => {
    return state.inquiryList.isLoading;
  });
};

export const useIsConflictErrorModalOpenSelector = () => {
  return useAppSelector((state) => state.inquiryList.isConflictErrorModalOpen);
};
